import React, { useEffect, useState } from 'react';
import { BellRing, Bot, MessageCircle, Plug, Send, ShieldAlert, Trash2, Plus } from 'lucide-react';
import {
  Alert, Badge, Button, Card, CardHeader, ConfirmDialog, Field, Input,
  SectionTitle, Select, Textarea
} from '../ui';
import { adminService } from '../../services/agencyService';

// ⭐ Operaciones de IA y MCP (spec §8).
//
// El punto entero de esta pantalla es que la IA NO ejecuta nada por su cuenta.
// Cuando aquí se pulsa "Proponer", el backend crea una solicitud en estado
// pendiente — exactamente igual que si la hubiera escrito un cliente — y esa
// solicitud tiene que pasar por el mismo flujo de revisión y aprobación
// humana antes de que nada cambie. Este archivo no tiene, y no debe tener,
// ninguna llamada que aplique un cambio directamente.
//
// Se muestran operaciones estructuradas (no texto libre) por dos razones:
// son auditables campo por campo, y permiten mostrarle al administrador
// exactamente qué se va a cambiar antes de aprobar.

// Catálogo de operaciones permitidas. Es una lista cerrada a propósito: si la
// IA pudiera inventar tipos de operación, "controlada" dejaría de significar
// nada. Los `fields` son lo que el ejecutor del backend sabe interpretar.
const OPERATIONS = [
  {
    type: 'update_client_profile',
    label: 'Actualizar perfil del cliente',
    description: 'Cambia datos comerciales de la ficha (sector, responsable, dominio, notas).',
    requiresApproval: true,
    fields: [{ key: 'industry', label: 'Sector', placeholder: 'Restaurante' }, { key: 'primary_domain', label: 'Dominio', placeholder: 'https://ejemplo.com' }]
  },
  {
    type: 'update_site_page',
    label: 'Actualizar una página',
    description: 'Modifica el contenido de una página publicada.',
    requiresApproval: true,
    fields: [{ key: 'page_id', label: 'ID de la página', placeholder: '12' }, { key: 'name', label: 'Nombre de la página', placeholder: 'Inicio' }]
  },
  {
    type: 'update_workflow_step',
    label: 'Actualizar un paso de automatización',
    description: 'Ajusta un paso concreto de un workflow existente.',
    requiresApproval: true,
    fields: [{ key: 'step_id', label: 'ID del paso' }, { key: 'config', label: 'Configuración (JSON)', placeholder: '{"message":"Hola"}' }]
  },
  {
    type: 'set_workflow_active',
    label: 'Activar o desactivar una automatización',
    description: 'Enciende o apaga un workflow completo.',
    requiresApproval: true,
    fields: [{ key: 'workflow_id', label: 'Automatización' }, { key: 'active', label: 'Activo (si/no)', placeholder: 'si' }]
  },
  {
    type: 'update_custom_field',
    label: 'Actualizar un campo personalizado',
    description: 'Cambia la definición de un campo del CRM.',
    requiresApproval: true,
    fields: [{ key: 'field_id', label: 'ID del campo' }, { key: 'label', label: 'Nuevo nombre' }]
  },
  {
    type: 'update_ai_context',
    label: 'Actualizar contexto de IA',
    description: 'Ajusta las instrucciones que usa el asistente del cliente.',
    requiresApproval: true,
    fields: [{ key: 'context_id', label: 'ID del contexto' }, { key: 'content', label: 'Nuevo contenido' }]
  },
  {
    type: 'update_message_template',
    label: 'Actualizar plantilla de mensaje',
    description: 'Cambia el texto de una plantilla automática.',
    requiresApproval: true,
    fields: [{ key: 'template_id', label: 'ID de la plantilla' }, { key: 'content', label: 'Nuevo texto' }]
  }
];

export default function OperacionesIA({ clients, onProposed }) {
  const [clientId, setClientId] = useState('');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [reason, setReason] = useState('');
  const [operations, setOperations] = useState([{ type: OPERATIONS[0].type, params: {} }]);
  const [notice, setNotice] = useState(null);
  const [busy, setBusy] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [tools, setTools] = useState(null);
  const [chatMessage, setChatMessage] = useState('');
  const [conversationId, setConversationId] = useState(null);
  const [chatHistory, setChatHistory] = useState([]);
  const [notificationSettings, setNotificationSettings] = useState(null);
  const [notificationBusy, setNotificationBusy] = useState(false);
  const [notificationNotice, setNotificationNotice] = useState(null);
  const [workflowTestClientId, setWorkflowTestClientId] = useState('');
  const [workflowTest, setWorkflowTest] = useState({ workflow_name: 'Ganado → aviso al dueño', stage_name: 'Ganado', notification_content: '✅ {{contact.name}} llegó a Ganado. Teléfono: {{contact.phone}}' });
  const [workflowTestBusy, setWorkflowTestBusy] = useState(false);
  const [workflowConfirmation, setWorkflowConfirmation] = useState(null);
  const [workflowTestNotice, setWorkflowTestNotice] = useState(null);

  const client = clients.find((c) => String(c.id) === String(clientId));

  useEffect(() => {
    adminService.notificationSettings()
      .then(setNotificationSettings)
      .catch((error) => setNotificationNotice({ tone: 'danger', text: error.message }));
  }, []);

  const saveNotifications = async () => {
    setNotificationBusy(true);
    setNotificationNotice(null);
    try {
      const saved = await adminService.saveNotificationSettings(notificationSettings);
      setNotificationSettings(saved);
      setNotificationNotice({ tone: 'success', text: 'Avisos guardados.' });
    } catch (error) {
      setNotificationNotice({ tone: 'danger', text: error.message });
    } finally {
      setNotificationBusy(false);
    }
  };

  const testNotifications = async () => {
    setNotificationBusy(true);
    setNotificationNotice(null);
    try {
      await adminService.testNotifications();
      setNotificationNotice({ tone: 'success', text: 'Revisa tu WhatsApp: el mensaje de prueba fue enviado.' });
    } catch (error) {
      setNotificationNotice({ tone: 'danger', text: error.message });
    } finally {
      setNotificationBusy(false);
    }
  };

  const proposeWorkflowTest = async (notificationRecipient) => {
    const selectedClient = clients.find((item) => String(item.id) === String(workflowTestClientId));
    if (!selectedClient) {
      setWorkflowTestNotice({ tone: 'warning', text: 'Selecciona el cliente para esta prueba.' });
      return;
    }
    if (!workflowTest.workflow_name.trim() || !workflowTest.stage_name.trim()) {
      setWorkflowTestNotice({ tone: 'warning', text: 'Escribe el nombre de la automatización y la etapa que la activa.' });
      return;
    }
    setWorkflowTestBusy(true);
    setWorkflowTestNotice(null);
    try {
      const response = await adminService.mcpCall('propose_workflow_instruction', {
        client_id: Number(selectedClient.id),
        client_name: selectedClient.name,
        action: 'create',
        workflow_name: workflowTest.workflow_name.trim(),
        stage_name: workflowTest.stage_name.trim(),
        notification_content: workflowTest.notification_content.trim(),
        ...(notificationRecipient ? { notification_recipient: notificationRecipient } : {})
      });
      const result = JSON.parse(response.content?.[0]?.text || '{}');
      if (result.status === 'needs_notification_confirmation') {
        setWorkflowConfirmation(result.notification_confirmation);
        return;
      }
      if (result.status === 'cancelled_by_client') {
        setWorkflowConfirmation(null);
        setWorkflowTestNotice({ tone: 'info', text: 'Prueba cancelada: no se creó ninguna automatización.' });
        return;
      }
      if (result.status === 'pending_approval') {
        setWorkflowConfirmation(null);
        setWorkflowTestNotice({ tone: 'success', text: `La propuesta #${result.request_id} quedó pendiente. Ve a Solicitudes, revísala y usa “Aprobar y aplicar”.` });
        onProposed?.();
        return;
      }
      throw new Error('La respuesta del MCP no se pudo interpretar.');
    } catch (error) {
      setWorkflowTestNotice({ tone: 'danger', text: error.message });
    } finally {
      setWorkflowTestBusy(false);
    }
  };

  const setOperation = (index, patch) => {
    setOperations(operations.map((op, i) => (i === index ? { ...op, ...patch } : op)));
  };

  const propose = async () => {
    setBusy(true);
    setNotice(null);
    try {
      const parsedOperations = operations.map((operation) => {
        const params = { ...(operation.params || {}) };
        if (typeof params.active === 'string') params.active = ['si', 'sí', 'true', '1'].includes(params.active.trim().toLowerCase());
        if (typeof params.config === 'string' && params.config.trim()) {
          try { params.config = JSON.parse(params.config); } catch (_) { throw new Error('La configuración del paso debe ser JSON válido.'); }
        }
        return { ...operation, params };
      });
      await adminService.proposeChange({
        client_id: Number(clientId),
        title: title.trim(),
        description: `${description.trim()}\n\nMotivo: ${reason.trim() || 'No especificado'}`,
        requested_changes: { operations: parsedOperations },
        source: 'copiloto'
      });
      setNotice({
        tone: 'success',
        text: 'Propuesta registrada como solicitud pendiente. Revísala y apruébala en Solicitudes para que se ejecute.'
      });
      setTitle(''); setDescription(''); setReason('');
      setOperations([{ type: OPERATIONS[0].type, params: {} }]);
      setConfirming(false);
      onProposed?.();
    } catch (err) {
      setNotice({ tone: 'danger', text: err.message });
      setConfirming(false);
    } finally {
      setBusy(false);
    }
  };

  const loadTools = async () => {
    setBusy(true);
    try {
      const result = await adminService.mcpListTools();
      setTools(result.tools || []);
    } catch (err) {
      setNotice({ tone: 'danger', text: err.message });
    } finally {
      setBusy(false);
    }
  };

  const askCopilot = async () => {
    if (!clientId || !chatMessage.trim()) return;
    setBusy(true);
    setNotice(null);
    const outgoing = chatMessage.trim();
    try {
      const result = await adminService.chatWithAi({ client_id: Number(clientId), conversation_id: conversationId, message: outgoing });
      setConversationId(result.conversation_id);
      setChatHistory((current) => [...current, { role: 'user', content: outgoing }, result.message]);
      setChatMessage('');
    } catch (err) {
      setNotice({ tone: 'danger', text: err.message });
    } finally {
      setBusy(false);
    }
  };

  const valid = clientId && title.trim() && description.trim();

  return (
    <div className="space-y-5">
      <Alert tone="warning" title="La IA propone, MARKETOS decide">
        Nada de lo que se prepare aquí se aplica al momento. Cada propuesta entra como una solicitud
        pendiente y necesita aprobación humana antes de ejecutarse.
      </Alert>

      <Card className="overflow-hidden border-sky-100 bg-gradient-to-br from-white via-sky-50 to-magenta-50/40">
        <CardHeader
          icon={<BellRing className="h-5 w-5" />}
          title="Avisos por WhatsApp"
          subtitle="Recibe las solicitudes nuevas y avisa automáticamente al cliente cuando el cambio quede listo."
        />
        <div className="space-y-4 bg-gradient-to-r from-white/80 via-sky-50/60 to-white/80 p-4 sm:p-5">
          {notificationNotice && <Alert tone={notificationNotice.tone} onDismiss={() => setNotificationNotice(null)}>{notificationNotice.text}</Alert>}
          {!notificationSettings ? (
            <p className="text-sm text-navy-900/55">Cargando tus WhatsApp conectados…</p>
          ) : notificationSettings.channels.length === 0 ? (
            <Alert tone="warning" title="Primero conecta un WhatsApp">
              Ve a la pestaña WhatsApp de Operación MARKETOS, conecta el número empresarial con el código QR y vuelve a esta pantalla.
            </Alert>
          ) : (
            <>
              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="WhatsApp que enviará los avisos" required>
                  <Select
                    value={notificationSettings.channel_id || ''}
                    onChange={(event) => setNotificationSettings({ ...notificationSettings, channel_id: Number(event.target.value) || null })}
                  >
                    <option value="">Selecciona un WhatsApp</option>
                    {notificationSettings.channels.map((channel) => (
                      <option key={channel.id} value={channel.id}>
                        {channel.name} · {channel.client_name}{channel.connected ? ' · Conectado' : ' · Desconectado'}
                      </option>
                    ))}
                  </Select>
                </Field>
                <Field label="Tu WhatsApp" required hint="Incluye el código de país. Ej.: 593...">
                  <Input
                    inputMode="tel"
                    value={notificationSettings.admin_whatsapp || ''}
                    onChange={(event) => setNotificationSettings({ ...notificationSettings, admin_whatsapp: event.target.value })}
                    placeholder="593999999999"
                  />
                </Field>
              </div>
              <div className="flex flex-wrap justify-end gap-3 border-t border-sky-100 pt-4">
                <Button
                  variant="secondary"
                  className="!bg-[#0A80C9] !text-white hover:!bg-[#076AA8]"
                  loading={notificationBusy}
                  onClick={testNotifications}
                >
                  Probar mi WhatsApp
                </Button>
                <Button
                  className="!bg-[#E5007D] !text-white hover:!bg-[#B80064]"
                  loading={notificationBusy}
                  onClick={saveNotifications}
                >
                  Guardar avisos
                </Button>
              </div>
            </>
          )}
        </div>
      </Card>

      <Card>
        <CardHeader
          icon={<MessageCircle className="h-5 w-5" />}
          title="Chat de IA operativa"
          subtitle="Analiza el contexto del cliente y deja un historial. La conexión con un proveedor externo se habilita únicamente cuando MARKETOS autorice la política de datos."
        />
        <div className="space-y-3 p-4 sm:p-5">
          {!clientId && <Alert tone="warning">Selecciona primero un cliente arriba para iniciar una conversación aislada.</Alert>}
          {chatHistory.length > 0 && (
            <div className="max-h-72 space-y-2 overflow-y-auto rounded-xl bg-cream p-3">
              {chatHistory.map((message, index) => (
                <div key={index} className={`rounded-xl p-3 text-sm ${message.role === 'user' ? 'ml-8 bg-navy text-white' : 'mr-8 bg-white text-navy-900 shadow-sm'}`}>
                  <p className="mb-1 text-[10px] font-bold uppercase tracking-wide opacity-60">{message.role === 'user' ? 'Tú' : 'Copiloto MARKETOS'}</p>
                  <p>{message.content}</p>
                </div>
              ))}
            </div>
          )}
          <Field label="Consulta para el copiloto" hint="Ej.: revisa qué falta para que este cliente pueda captar contactos desde su landing.">
            <Textarea rows={3} value={chatMessage} disabled={!clientId || busy} onChange={(event) => setChatMessage(event.target.value)} />
          </Field>
          <div className="flex justify-end">
            <Button variant="soft" icon={<Send className="h-4 w-4" />} disabled={!clientId || !chatMessage.trim()} loading={busy} onClick={askCopilot}>Analizar</Button>
          </div>
        </div>
      </Card>

      <Card>
        <CardHeader
          icon={<Bot className="h-5 w-5" />}
          title="Preparar una operación"
          subtitle="Describe qué se cambiaría y por qué. Quedará esperando tu aprobación."
        />
        <div className="space-y-4 p-4 sm:p-5">
          {notice && <Alert tone={notice.tone} onDismiss={() => setNotice(null)}>{notice.text}</Alert>}

          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Cliente afectado" required>
              <Select value={clientId} onChange={(event) => {
                setClientId(event.target.value);
                setConversationId(null);
                setChatHistory([]);
              }}>
                <option value="">Selecciona un cliente</option>
                {clients.map((c) => <option key={c.id} value={c.id}>{c.display_name || c.name}</option>)}
              </Select>
            </Field>
            <Field label="Título" required>
              <Input value={title} onChange={(event) => setTitle(event.target.value)} placeholder="Ej.: Actualizar el título de la página de inicio" />
            </Field>
          </div>

          <Field label="Qué se va a cambiar" required hint="En lenguaje corriente. Esto es lo que leerás al aprobar.">
            <Textarea rows={3} value={description} onChange={(event) => setDescription(event.target.value)} />
          </Field>

          <Field label="Por qué se va a cambiar" hint="El motivo queda registrado en la auditoría junto con la operación.">
            <Textarea rows={2} value={reason} onChange={(event) => setReason(event.target.value)} placeholder="Ej.: El cliente cambió su propuesta de valor." />
          </Field>

          <div>
            <SectionTitle hint="Solo se permiten operaciones de esta lista. La IA no puede inventar otras.">
              Operaciones
            </SectionTitle>
            <div className="space-y-3">
              {operations.map((operation, index) => {
                const definition = OPERATIONS.find((o) => o.type === operation.type) || OPERATIONS[0];
                return (
                  <div key={index} className="rounded-xl border border-navy-50 bg-cream p-4">
                    <div className="flex items-start gap-2">
                      <div className="min-w-0 flex-1">
                        <Select
                          value={operation.type}
                          onChange={(event) => setOperation(index, { type: event.target.value, params: {} })}
                          aria-label="Tipo de operación"
                        >
                          {OPERATIONS.map((option) => <option key={option.type} value={option.type}>{option.label}</option>)}
                        </Select>
                        <p className="mt-1.5 text-xs text-navy-900/55">{definition.description}</p>
                      </div>
                      {operations.length > 1 && (
                        <button
                          type="button"
                          aria-label="Quitar operación"
                          onClick={() => setOperations(operations.filter((_, i) => i !== index))}
                          className="shrink-0 rounded-lg p-2 text-navy-900/40 hover:bg-magenta-50 hover:text-magenta-700"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      )}
                    </div>

                    <div className="mt-3 grid gap-3 sm:grid-cols-2">
                      {definition.fields.map((field) => (
                        <Field key={field.key} label={field.label}>
                          <Input
                            value={operation.params?.[field.key] || ''}
                            placeholder={field.placeholder}
                            onChange={(event) => setOperation(index, { params: { ...operation.params, [field.key]: event.target.value } })}
                          />
                        </Field>
                      ))}
                    </div>

                    <div className="mt-3 flex flex-wrap items-center gap-2 text-xs">
                      <Badge tone="magenta"><ShieldAlert className="h-3 w-3" /> Requiere aprobación</Badge>
                      {client && <Badge tone="sky">Afecta a {client.display_name || client.name}</Badge>}
                    </div>
                  </div>
                );
              })}
            </div>

            <Button
              variant="ghost" size="sm" className="mt-3" icon={<Plus className="h-4 w-4" />}
              onClick={() => setOperations([...operations, { type: OPERATIONS[0].type, params: {} }])}
            >
              Agregar otra operación
            </Button>
          </div>

          <div className="flex justify-end">
            <Button icon={<Send className="h-4 w-4" />} disabled={!valid} onClick={() => setConfirming(true)}>
              Proponer para aprobación
            </Button>
          </div>
        </div>
      </Card>

      <Card>
        <CardHeader
          icon={<Plug className="h-5 w-5" />}
          title="Conexión MCP"
          subtitle="Herramientas que un asistente externo puede usar sobre esta operación."
          actions={<Button variant="soft" size="sm" onClick={loadTools} loading={busy}>Comprobar conexión</Button>}
        />
        <div className="p-4 sm:p-5">
          <div className="mb-5 rounded-2xl border border-sky-100 bg-gradient-to-br from-sky-50 via-white to-magenta-50/50 p-4">
            <p className="font-bold text-navy-900">Prueba guiada: aviso cuando un contacto llega a una etapa</p>
            <p className="mt-1 text-sm text-navy-900/65">Prepara una propuesta real pero segura: no se activa hasta que tú la apruebes desde Solicitudes.</p>
            {workflowTestNotice && <div className="mt-3"><Alert tone={workflowTestNotice.tone} onDismiss={() => setWorkflowTestNotice(null)}>{workflowTestNotice.text}</Alert></div>}
            <div className="mt-4 grid gap-3 sm:grid-cols-2">
              <Field label="Cliente de prueba" required>
                <Select value={workflowTestClientId} onChange={(event) => { setWorkflowTestClientId(event.target.value); setWorkflowConfirmation(null); }}>
                  <option value="">Selecciona un cliente</option>
                  {clients.map((item) => <option key={item.id} value={item.id}>{item.display_name || item.name}</option>)}
                </Select>
              </Field>
              <Field label="Etapa que activa el aviso" required hint="Debe existir ya en el Pipeline del cliente.">
                <Input value={workflowTest.stage_name} onChange={(event) => setWorkflowTest({ ...workflowTest, stage_name: event.target.value })} placeholder="Ganado" />
              </Field>
              <Field label="Nombre de la automatización" required>
                <Input value={workflowTest.workflow_name} onChange={(event) => setWorkflowTest({ ...workflowTest, workflow_name: event.target.value })} />
              </Field>
              <Field label="Mensaje que recibirá el dueño" hint="Puedes usar {{contact.name}} y {{contact.phone}}.">
                <Input value={workflowTest.notification_content} onChange={(event) => setWorkflowTest({ ...workflowTest, notification_content: event.target.value })} />
              </Field>
            </div>
            {workflowConfirmation ? (
              <div className="mt-4 rounded-xl border border-magenta-100 bg-white p-4 shadow-sm">
                <p className="font-semibold text-navy-900">{workflowConfirmation.question}</p>
                <p className="mt-1 text-sm text-navy-900/60">No se muestra ni se envía el número completo al asistente.</p>
                <div className="mt-3 flex flex-wrap gap-2">
                  <Button className="!bg-[#0A80C9] !text-white hover:!bg-[#076AA8]" loading={workflowTestBusy} onClick={() => proposeWorkflowTest('saved')}>Usar este WhatsApp</Button>
                  <Button variant="soft" disabled={workflowTestBusy} onClick={() => setWorkflowTestNotice({ tone: 'info', text: 'El cliente puede cambiarlo en Mi Proyecto → Avisos. Luego vuelve a iniciar esta prueba.' })}>Cambiar el número</Button>
                  <Button variant="ghost" disabled={workflowTestBusy} onClick={() => proposeWorkflowTest('none')}>Cancelar aviso</Button>
                </div>
              </div>
            ) : (
              <div className="mt-4 flex justify-end">
                <Button className="!bg-[#102b70] !text-white hover:!bg-[#0a2055]" loading={workflowTestBusy} onClick={() => proposeWorkflowTest()}>
                  Preparar prueba
                </Button>
              </div>
            )}
          </div>
          {tools === null && <p className="text-sm text-navy-900/55">Pulsa «Comprobar conexión» para ver qué herramientas están disponibles.</p>}
          {tools?.length === 0 && <p className="text-sm text-navy-900/55">No hay herramientas MCP disponibles.</p>}
          {tools?.length > 0 && (
            <ul className="space-y-2">
              {tools.map((tool) => (
                <li key={tool.name} className="rounded-xl border border-navy-50 p-3">
                  <p className="font-semibold text-navy-900">{tool.name}</p>
                  <p className="text-sm text-navy-900/60">{tool.description}</p>
                </li>
              ))}
            </ul>
          )}
          <p className="mt-4 rounded-xl bg-cream p-3 text-xs text-navy-900/60">
            Incluso a través de MCP, ejecutar un cambio exige que la solicitud esté aprobada. El servidor
            lo verifica en cada llamada, no solo en esta pantalla.
          </p>
        </div>
      </Card>

      <ConfirmDialog
        open={confirming}
        loading={busy}
        title="Proponer para aprobación"
        confirmLabel="Proponer"
        onCancel={() => setConfirming(false)}
        onConfirm={propose}
        message={`Se creará una solicitud pendiente para ${client?.display_name || client?.name || 'el cliente'}. No se aplicará ningún cambio hasta que la apruebes.`}
      />
    </div>
  );
}
